import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Loader2Icon } from 'lucide-react';
import { buildPartGeometry } from '../utils/partGeometry';
import type { PartShape } from '../utils/partGeometry';
type ModelViewerProps = {
  /** STL or GLB/GLTF file. Empty → procedural stand-in for `shape`. */
  src: string;
  shape: PartShape;
  label: string;
};
const METAL = new THREE.MeshStandardMaterial({
  color: 0xc9c5c8,
  metalness: 0.55,
  roughness: 0.38
});
const EDGE = new THREE.LineBasicMaterial({
  color: 0x2a2328,
  transparent: true,
  opacity: 0.55
});
export function ModelViewer({
  src,
  shape,
  label
}: ModelViewerProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.touchAction = 'pan-y';
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(4.2, 3.2, 5.2);
    scene.add(new THREE.HemisphereLight(0xffffff, 0xb8b0b5, 1.4));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(5, 8, 6);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xffffff, 0.8);
    rim.position.set(-6, 2, -4);
    scene.add(rim);
    const group = new THREE.Group();
    scene.add(group);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.autoRotate = !reduce;
    controls.autoRotateSpeed = 1.2;
    const addGeometry = (g: THREE.BufferGeometry) => {
      group.add(new THREE.Mesh(g, METAL));
      group.add(new THREE.LineSegments(new THREE.EdgesGeometry(g, 28), EDGE));
    };
    const frame = () => {
      const box = new THREE.Box3().setFromObject(group);
      const size = box.getSize(new THREE.Vector3()).length() || 1;
      const center = box.getCenter(new THREE.Vector3());
      group.position.sub(center);
      group.scale.setScalar(4 / size);
      setStatus('ready');
    };
    if (!src) {
      buildPartGeometry(shape).forEach(addGeometry);
      frame();
    } else if (/\.stl(\?|$)/i.test(src)) {
      new STLLoader().load(src, g => {
        g.computeVertexNormals();
        g.rotateX(-Math.PI / 2);
        addGeometry(g);
        frame();
      }, undefined, () => setStatus('error'));
    } else {
      new GLTFLoader().load(src, gltf => {
        gltf.scene.traverse(o => {
          if (o instanceof THREE.Mesh) o.material = METAL;
        });
        group.add(gltf.scene);
        frame();
      }, undefined, () => setStatus('error'));
    }

    // Only render while the card is on screen.
    let visible = false;
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      controls.update();
      renderer.render(scene, camera);
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      cancelAnimationFrame(raf);
      if (visible) loop();
    });
    io.observe(mount);
    const ro = new ResizeObserver(() => {
      const {
        clientWidth: w,
        clientHeight: h
      } = mount;
      if (!w || !h) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    });
    ro.observe(mount);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      controls.dispose();
      group.traverse(o => {
        if (o instanceof THREE.Mesh || o instanceof THREE.LineSegments) o.geometry.dispose();
      });
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, [src, shape]);
  return <div ref={mountRef} role="img" aria-label={`3D-Modell: ${label}. Zum Drehen ziehen.`} className="relative h-full w-full cursor-grab active:cursor-grabbing" data-id="element-404" data-mp="1xj25r6d7ipal">
      {status !== 'ready' && <div className="absolute inset-0 grid place-items-center text-[13px] text-muted" data-id="element-405" data-mp="1nsp9igw157om">
          {status === 'loading' ? <Loader2Icon className="h-5 w-5 animate-spin" aria-label="Modell wird geladen" data-id="element-406" data-mp="2bi1j3hv9rhrg" /> : 'Modell konnte nicht geladen werden'}
        </div>}
    </div>;
}